import { get, isEmpty, merge } from 'lodash'
import { throwError, timer, range, zip, defer, from, of } from 'rxjs'
import { ajax } from 'rxjs/ajax'
import { map, mergeMap, retryWhen } from 'rxjs/operators'
import { toCamelCase, toSnakeCase } from 'case-converter'

import { ApiError } from 'classes/errors'
import {
  API_NOT_FOUND,
  APPLICATION_AUTHENTICATE_TOKEN,
  DPOP_INVALID,
  DPOP_REQUIRED,
  INVALID_USER_OR_TOKEN,
  NO_RESULTS,
  RATE_LIMIT,
  SESSION_EXPIRED,
  SESSION_IP_MISMATCH,
  CAN_NOT_CONNECT,
} from 'constants/apiErrorCodes'
import * as contentTypes from 'constants/contentTypes'
import * as contentTypesRegex from 'constants/contentTypesRegex'
import { createDpopProof, generateDpopKeyPair } from 'utils/dpop'
import { loadDpopKeyPair, saveDpopKeyPair } from 'utils/dpopKeyStore'
import sessionActionTypes from 'app_modules/session/constants'

const AJAX_ERROR = 'ajax error'
const MAX_TRIES = 2
const NOT_FOUND = 404
const RETRY_TIME = 500
// Internal-only code: DPoP proof lost after page refresh → force re-login with a specific message.
// Not a BE error code — never sent over the wire, only used inside retryWhen → subscribe$.
const DPOP_SESSION_EXPIRED = 'DPOP_SESSION_EXPIRED'

// Deduplicates the DPoP key-pair recovery after a page reload.
// When dpopActive=true but dpopKeyPair=null, every parallel request that fires at
// startup would independently load (or generate) a key pair. This shared promise ensures
// they all wait for the same one, so they all sign with the same key.
let dpopRebindPromise = null

const { API_ENCRYPTION_PASSPHRASE, API_RESPONSE_HAS_ENCRYPTION, API_URL, SENSAI_ENVIRONMENT } = process.env

const DEFAULT_CONTENT_TYPE = contentTypes.JSON
const API_VERSION = 2
const CONTENT_SECURITY_POLICY = 'policy'
const MAX_AGE = '31536000'
const X_CONTENT_TYPE_OPTIONS = 'nosniff'
const X_FRAME_OPTIONS = 'SAMEORIGIN'

const PENETRATION_TESTING_HEADERS = {
  'Content-Security-Policy': CONTENT_SECURITY_POLICY,
  'Strict-Transport-Security': `max-age=${MAX_AGE}; includeSubDomains; preload`,
  'X-Content-Type-Options': X_CONTENT_TYPE_OPTIONS,
  'X-Frame-Options': X_FRAME_OPTIONS,
}

/**
 * @class ApiEndpoint
 * @description Contains functions to send the request and process the response
 */
export default class ApiEndpoint {
  constructor(options) {
    merge(this, options)

    this.crypto = options.crypto || this.api.crypto
  }

  /**
   * @function retryWhen
   * @description Sends the request while it fails and the response is not a valid API error
   * @param {number} attempts
   */
  retryWhen(attempts) {
    return zip(attempts, range(1, MAX_TRIES + 1)).pipe(
      mergeMap(([errorString, i]) => {
        const { session } = this.api.store.getState()
        const isAuthenticated = get(session, 'isAuthenticated', false)

        const error = this.decryptResponse(errorString)

        if (i > MAX_TRIES) {
          if (typeof DEVELOPMENT !== 'undefined' && DEVELOPMENT) {
            console.log(error)
          }
          return throwError(() => new ApiError({ errorCode: CAN_NOT_CONNECT }))
        }

        const errorCode = get(error, 'errorCode')

        if (
          isAuthenticated &&
          (errorCode === INVALID_USER_OR_TOKEN ||
            errorCode === APPLICATION_AUTHENTICATE_TOKEN ||
            (errorString.status === 401 && errorString.responseType === 'blob'))
        ) {
          return throwError(() => new ApiError({ errorCode: SESSION_EXPIRED }))
        }

        if (isAuthenticated && errorCode === SESSION_IP_MISMATCH) {
          return throwError(() => new ApiError({ errorCode: SESSION_IP_MISMATCH }))
        }

        // DPoP errors (SIM-258): proof required or invalid — force re-login with a
        // message explaining that a page refresh requires re-authentication.
        // No isAuthenticated guard: parallel requests can race and the second one arrives
        // after expireSession has already set isAuthenticated=false. Guard in subscribe$ instead.
        if (errorCode === DPOP_REQUIRED || errorCode === DPOP_INVALID) {
          return throwError(() => new ApiError({ errorCode: DPOP_SESSION_EXPIRED }))
        }

        // ignore 404 error (used in empty lists)
        if (errorCode === API_NOT_FOUND && this.ignoreNotFound) {
          return throwError(() => new ApiError({ errorCode: NO_RESULTS }))
        }

        // if there are connection issues, we need to try again
        if (
          (isEmpty(error) && get(errorString, 'message') === AJAX_ERROR) ||
          (get(error, '_status') === NOT_FOUND && !errorCode)
        ) {
          return timer(i * RETRY_TIME)
        }

        // rate limit
        if (error._status === RATE_LIMIT) {
          this.api.rateLimit()
          return throwError(() => new ApiError({ errorCode: RATE_LIMIT }))
        }

        // is a valid api error, we don't need to try the request again
        return throwError(() => new ApiError(error))
      }),
    )
  }

  /**
   * @function setTimeout
   * @description sets the expire session timeout
   */
  setTimeout() {
    const { session } = this.api.store.getState()

    const { isAuthenticated } = session

    if (isAuthenticated) {
      this.api.setTimeout()
    }
  }

  /**
   * @function request
   * @description Sends a backend request. Returns a Subscription (synchronous).
   * DPoP proof generation is async but handled inside the RxJS Observable pipeline
   * via defer() + from() + mergeMap so the outer interface stays synchronous.
   * @param {object} options - Request Options
   * @param {function} onSuccess - Success callback function
   * @param {function} onError - Error callback function
   */
  request(options, onSuccess, onError) {
    const settings = this.settings(options)
    let contentType = {}

    const { forceDefaultPassphrase, useOptionsToken } = this.encrypt || {}

    const { user } = this.api.store?.getState() || {}

    const { token, impersonateToken, ssoToken } = useOptionsToken ? options : user || {}

    // dpopKeyPair and dpopActive are always taken from Redux state — never passed through options
    const { dpopKeyPair, dpopActive } = user || {}

    const hasEmptyToken = isEmpty(token) && isEmpty(impersonateToken) && isEmpty(ssoToken)

    let Authorization = get(settings.headers, 'Authorization', '')

    if (token) {
      Authorization = `Token token=${token}`
    } else if (impersonateToken) {
      Authorization = `Token impersonate=${impersonateToken}`
    } else if (ssoToken) {
      Authorization = `Token sso=${ssoToken}`
    }

    if (forceDefaultPassphrase || hasEmptyToken) {
      this.crypto.setPassphrase(API_ENCRYPTION_PASSPHRASE)
    } else {
      this.crypto.setPassphrase(token || impersonateToken || ssoToken)
    }

    let { body } = settings

    if (body && get(settings.headers, 'enctype') !== contentTypes.FORM) {
      body = JSON.stringify(toSnakeCase(body))
      contentType = {
        'Content-Type': DEFAULT_CONTENT_TYPE,
      }
    }

    // Build request settings without the DPoP header — it will be injected by the
    // Observable pipeline below so that proof generation stays inside the stream.
    const baseSettings = this.encryptRequest({
      responseType: '*',
      ...settings,
      body,
      headers: {
        Accept: `application/json; version=${API_VERSION}`,
        // settings.headers is spread first; this preserves the login pre-built DPoP
        // proof that session.controller.js sets before there is a Redux token.
        ...(settings.headers || {}),
        Authorization,
        ...contentType,
        ...PENETRATION_TESTING_HEADERS,
        ...(settings?.endpoint?.includes('sensai') && { env: SENSAI_ENVIRONMENT }),
      },
      url: `${API_URL}${settings.endpoint}`,
    })

    // DPoP (SIM-258): build an Observable that emits the extra headers to merge in.
    // - Authenticated + non-impersonation + key pair present: sign a proof.
    // - Authenticated + dpopActive + no key pair (page refresh after Stripe redirect or
    //   any full-page navigation): generate a new key pair inline, dispatch it to Redux
    //   so subsequent requests use it, then sign the proof. All parallel requests that
    //   arrive before the key pair is ready share dpopRebindPromise so only one is generated.
    //   The backend accepts the new key via TOFU re-binding (key rotation).
    // - Authenticated + no key pair + dpopActive=false: soft-mode session — no proof needed.
    // - Unauthenticated (login) or impersonation: emit {} so no DPoP header is added.
    //   The login proof is already in baseSettings.headers via settings.headers above.
    //
    // Every proof is built inside defer() so each subscription signs a fresh one.
    // retryWhen resubscribes to source$ on connection errors; with a bare from(promise)
    // every retry would resend the first attempt's proof. If that attempt reached the
    // backend, its jti is already recorded, so the retry is rejected as a replay (644)
    // and the user is logged out. New proof per attempt = new jti and current iat.
    let dpopProof$
    const htu = `${API_URL}${settings.endpoint}`.split('?')[0]
    const method = settings.method || 'GET'
    const activeToken = token || ssoToken
    if (!hasEmptyToken && !impersonateToken && dpopKeyPair) {
      // Pass the active token as accessToken so the ath claim is included per RFC 9449 §4.2.
      // token takes precedence over ssoToken — only one is ever non-null at a time.
      // externalRequest() (SenSai AI) is intentionally excluded: it targets a separate
      // external service that does not validate DPoP proofs.
      dpopProof$ = defer(() =>
        from(
          createDpopProof(dpopKeyPair, method, htu, activeToken)
            .then(proof => ({ DPoP: proof }))
            .catch(() => ({})),
        ),
      )
    } else if (!hasEmptyToken && !impersonateToken && dpopActive) {
      // Key pair was in use but is no longer in Redux memory: the page was reloaded.
      //
      // 1. Reuse the key persisted in IndexedDB at login (EEDTD-101). Its fingerprint
      //    still matches the one the backend bound to this session, so the session
      //    survives the reload. This is the normal path.
      // 2. Only when nothing is stored (site data cleared, IndexedDB unavailable) fall
      //    back to generating a new pair. A password session can re-bind to it; a bound
      //    SSO session cannot and answers 644, which ends in a clean re-login. That is
      //    the intended secure outcome, not a bug: rebinding a session to a key the
      //    client invented would let anyone holding a stolen token do the same.
      if (!dpopRebindPromise) {
        const tokenAtRebindStart = activeToken
        dpopRebindPromise = loadDpopKeyPair()
          .then(storedKeyPair => {
            if (storedKeyPair) {
              return { keyPair: storedKeyPair, isNew: false }
            }
            return generateDpopKeyPair().then(keyPair => ({ keyPair, isNew: true }))
          })
          .then(({ keyPair: recoveredKeyPair, isNew }) => {
            // Guard: if the session changed while the key was being loaded or generated
            // (e.g. logout, token refresh), discard the pair: it belongs to a stale
            // session and would wrongly flip dpopActive back to true.
            const currentToken =
              (this.api.store.getState().user || {}).token || (this.api.store.getState().user || {}).ssoToken
            if (currentToken !== tokenAtRebindStart) {
              dpopRebindPromise = null
              return null
            }
            this.api.store.dispatch({
              type: sessionActionTypes.SET_DPOP_KEY_PAIR,
              payload: { dpopKeyPair: recoveredKeyPair },
            })
            if (isNew) {
              // Persist the fallback key so the next reload reuses it. Fire-and-forget:
              // saveDpopKeyPair never rejects and this request does not depend on it.
              saveDpopKeyPair(recoveredKeyPair)
            }
            dpopRebindPromise = null
            return recoveredKeyPair
          })
          .catch(() => {
            dpopRebindPromise = null
            return null
          })
      }
      // Captured locally: the shared promise is reset to null once it settles, but a retry
      // of this request must still sign with the key it recovered.
      const rebindPromise = dpopRebindPromise
      dpopProof$ = defer(() =>
        from(
          rebindPromise.then(newKeyPair => {
            if (!newKeyPair) return {}
            return createDpopProof(newKeyPair, method, htu, activeToken)
              .then(proof => ({ DPoP: proof }))
              .catch(() => ({}))
          }),
        ),
      )
    } else {
      dpopProof$ = of({})
    }

    // Merge the proof headers into the request and execute via the shared subscribe pipeline.
    const source$ = dpopProof$.pipe(
      mergeMap(dpopHeaders =>
        ajax({
          ...baseSettings,
          headers: {
            ...baseSettings.headers,
            // dpopHeaders is spread last so it always wins over any conflicting header
            ...dpopHeaders,
          },
        }),
      ),
    )

    return this.subscribe$(source$, onSuccess, onError)
  }

  /**
   * @function externalRequest
   * @description Sends a request to external APIs (e.g., Sensai AI endpoints)
   * @param {object} options - Request Options
   * @param {function} onSuccess - Success callback function
   * @param {function} onError - Error callback function
   */
  externalRequest(options, onSuccess, onError) {
    const settings = this.settings(options)
    let contentType = {}

    const { user } = this.api.store?.getState() || {}
    const { token, impersonateToken, ssoToken } = user || {}

    let Authorization = ''
    if (token) {
      Authorization = `Bearer token=${token}`
    } else if (impersonateToken) {
      Authorization = `Bearer impersonate=${impersonateToken}`
    } else if (ssoToken) {
      Authorization = `Bearer sso=${ssoToken}`
    }

    let { body } = settings

    if (body && get(settings.headers, 'enctype') !== contentTypes.FORM) {
      body = JSON.stringify(body)
      contentType = {
        'Content-Type': DEFAULT_CONTENT_TYPE,
      }
    }

    const requestSettings = {
      responseType: '*',
      ...settings,
      body,
      headers: {
        Accept: `application/json; version=${API_VERSION}`,
        ...contentType,
        Authorization,
        env: SENSAI_ENVIRONMENT,
        ...(settings.headers || {}),
      },
      url: settings.baseURL + settings.endpoint,
    }

    // Log headers for debugging

    return this.ajax$(requestSettings, onSuccess, onError)
  }

  /**
   * @description Encrypts Request
   * @param {object} settings
   * @returns {object} settings with encrypted body (if it has encryption)
   */
  encryptRequest(settings) {
    if (API_RESPONSE_HAS_ENCRYPTION && settings.body && settings.body.constructor !== FormData) {
      const settingsEncrypt = {
        body: this.crypto.encrypt(settings.body),
        headers: {
          'Content-Type': contentTypes.TEXT_PLAIN,
        },
      }

      return merge({}, settings, settingsEncrypt)
    }
    return settings
  }

  /**
   * @description Decrypt response
   * @param {AjaxResponse} ajaxResponse - Pipe response
   * @throws {Observable}
   * @returns {AjaxResponse} Camel case decrypted response
   */
  decryptResponse(ajaxResponse) {
    const { response, status, xhr } = ajaxResponse

    const contentType = xhr.getResponseHeader('Content-Type')

    if (response === null) {
      throw new Error('There is not valid response')
    }

    if (contentTypesRegex.TEXT_PLAIN.test(contentType)) {
      return {
        ...toCamelCase(this.crypto.decrypt(response)),
        _status: status,
      }
    }
    if (contentTypesRegex.APPLICATION_JSON.test(contentType)) {
      let jsonResponse = null
      try {
        jsonResponse = toCamelCase(JSON.parse(response))
      } catch (error) {
        if (typeof DEVELOPMENT !== 'undefined' && DEVELOPMENT) {
          console.log('Server is returning an invalid JSON')
        }
        jsonResponse = {}
      }
      return {
        ...jsonResponse,
        _status: status,
      }
    }

    return response
  }

  /**
   * @description Creates an observable for an Ajax request with either a settings object with url,
   * headers, etc or a string for a URL. Delegates to subscribe$() for the shared
   * retry/decrypt/callback pipeline.
   * @param {object} - settings - An object with the request settings.
   * @param {Boolean} settings.async - Whether the request is async. The default is true.
   * @param {Object} settings.body - Optional body
   * @param {Boolean} settings.crossDomain - true if to use CORS, else false. The default is false.
   * @param {Boolean} settings.withCredentials - true if to use CORS withCredentials, else false.
   * The default is false.
   * @param {Object} settings.headers - Optional headers
   * @param {String} settings.method - Method of the request, such as GET, POST, PUT, PATCH, DELETE.
   * The default is GET.
   * @param {String} settings.password - The password for the request.
   * @param {Observer} settings.progressObserver - An optional Observer which listen to XHR2
   * progress events or error timeout values.
   * @param {String} settings.responseType - The response type. Either can be 'json', 'text' or
   * 'blob'. The default is 'text'
   * @param {Number} settings.timeout - Number - a number representing the number of milliseconds a
   * request can take before automatically being terminated. A value of 0 (which is the default)
   * means there is no timeout.
   * @param {String} settings.url - URL of the request
   * @param {String} settings.user - The user for the request
   * @param {function} onSuccess
   * @param {function} onError
   * {@link https://github.com/Reactive-Extensions/RxJS-DOM/blob/master/doc/operators/ajax.md}
   */
  ajax$(settings, onSuccess, onError) {
    return this.subscribe$(ajax(settings), onSuccess, onError)
  }

  /**
   * @description Shared subscribe pipeline used by both request() and ajax$().
   * Applies retry-on-error, response decryption, and the success/error callbacks.
   * @param {Observable} source$ - An RxJS Observable that emits an AjaxResponse
   * @param {function} onSuccess
   * @param {function} onError
   */
  subscribe$(source$, onSuccess, onError) {
    return source$
      .pipe(
        retryWhen(this.retryWhen.bind(this)),
        map(response => this.decryptResponse(response)),
      )
      .subscribe(
        response => {
          if (this.clearTimeout) {
            this.api.clearTimeout()
          } else {
            this.setTimeout()
          }
          if (this.afterResponse) {
            this.afterResponse(response, onSuccess)
          } else if (onSuccess) {
            onSuccess(response)
          }
        },
        error => {
          const { errorCode = '' } = error
          switch (errorCode) {
            case NO_RESULTS:
              this.setTimeout()
              onSuccess({})
              break
            case RATE_LIMIT:
              onSuccess({})
              this.api.rateLimit()
              break
            case SESSION_EXPIRED:
              this.api.dispatchExpireSession()
              break
            case DPOP_SESSION_EXPIRED:
              // Only fire expireSession once — the first parallel request to arrive does it.
              // Subsequent parallel requests see isAuthenticated=false and drop silently.
              if (get(this.api.store.getState(), 'session.isAuthenticated', false)) {
                this.api.dispatchExpireSession('dpopSessionExpired')
              }
              break
            case SESSION_IP_MISMATCH:
              if (get(this.api.store.getState(), 'session.isAuthenticated', false)) {
                this.handleIpMismatch()
              }
              // When already unauthenticated, drop silently — the user is already
              // being redirected to login by the first 642 response.
              break
            case CAN_NOT_CONNECT:
              this.api.dispatchConnectionError()
              onError({ error, isConnectionError: true })
              break
            default:
              onError({ error })
          }
        },
      )
  }

  handleIpMismatch() {
    this.api.dispatchExpireSession('sessionIpMismatch')
  }
}
